import { useEffect, useMemo, useState } from 'react';
import { useCrmData } from '../../contexts/CrmDataContext';
import { useAuth } from '../../contexts/AuthContext';
import { usePortal } from '../../contexts/PortalContext';
import { formatSupabaseError } from '../../lib/formatSupabaseError';
import { logPortalActivityQuiet } from '../../lib/portalActivityLog';
import {
  isMeetingMinutePosted,
  minuteAttendees,
  minuteDiscussion,
  minuteStatusLabel,
} from '../../lib/meetingMinutesHelpers';
import {
  projectTitleFromMinute,
  taskDescriptionFromMinute,
  taskTitleFromMinute,
} from '../../lib/meetingMinutesPrefill';
import { getDbSupabase } from '../../lib/supabase';
import type { Client, MeetingMinute, Task } from '../../types/crm';
import { idsMatch } from '../../utils/crmHelpers';
import { formatSingaporeDateTime } from '../../utils/dateTime';
import { ownerIdForUser } from '../../utils/ownerId';
import {
  datetimeLocalSingaporeToIso,
  defaultDatetimeLocalSingapore,
  toDatetimeLocalSingapore,
} from '../../utils/taskHelpers';

export interface MeetingMinuteSelection {
  id: string | null;
  isNew: boolean;
}

interface AccountMeetingMinutesPanelProps {
  client: Client;
  canEdit: boolean;
  selection: MeetingMinuteSelection;
  onSelectionChange: (next: MeetingMinuteSelection) => void;
  onAddProject: (prefill: { title: string }) => void;
  onAddTask: (prefill: { title: string; description: string; meetingMinuteId: string }) => void;
}

interface MinuteDraft {
  title: string;
  meetingLocal: string;
  attendees: string;
  discussion: string;
}

const emptyDraft = (): MinuteDraft => ({
  title: 'Meeting minutes',
  meetingLocal: defaultDatetimeLocalSingapore(),
  attendees: '',
  discussion: '',
});

function draftFromMinute(m: MeetingMinute): MinuteDraft {
  return {
    title: m.title,
    meetingLocal: toDatetimeLocalSingapore(m.meeting_date ?? m.created_at),
    attendees: minuteAttendees(m),
    discussion: minuteDiscussion(m),
  };
}

function MeetingMinutesReadOnlyView({
  minute,
  linkedTasks,
  canEdit,
  onEdit,
  onAddProject,
  onAddTask,
}: {
  minute: MeetingMinute;
  linkedTasks: Task[];
  canEdit: boolean;
  onEdit: () => void;
  onAddProject: () => void;
  onAddTask: () => void;
}) {
  const posted = isMeetingMinutePosted(minute);
  return (
    <article className="minutes-record">
      <header className="minutes-record__head">
        <div>
          <span
            className={`minutes-record__status-badge${posted ? ' minutes-record__status-badge--posted' : ' minutes-record__status-badge--draft'}`}
          >
            {minuteStatusLabel(minute)}
          </span>
          <h3 className="minutes-record__title">{minute.title}</h3>
          <p className="account-side-muted">
            {formatSingaporeDateTime(minute.meeting_date ?? minute.created_at)}
            {minute.author_name ? ` · ${minute.author_name}` : ''}
            {minute.posted_at ? ` · Posted ${formatSingaporeDateTime(minute.posted_at)}` : ''}
          </p>
        </div>
        {canEdit && posted && (
          <button type="button" className="btn-secondary btn-sm" onClick={onEdit}>
            Edit
          </button>
        )}
      </header>

      <section className="minutes-record__section">
        <h4 className="minutes-record__section-title">Attendees</h4>
        <div className="minutes-record__section-body">
          {minuteAttendees(minute) || <em className="account-side-muted">—</em>}
        </div>
      </section>

      <section className="minutes-record__section">
        <h4 className="minutes-record__section-title">Discussion</h4>
        <div className="minutes-record__section-body minutes-record__section-body--pre">
          {minuteDiscussion(minute) || <em className="account-side-muted">—</em>}
        </div>
      </section>

      {linkedTasks.length > 0 ? (
        <section className="minutes-record__section">
          <h4 className="minutes-record__section-title">Linked tasks</h4>
          <ul className="minutes-record__task-list">
            {linkedTasks.map((task) => (
              <li key={task.id} className="minutes-record__task-item">
                <span className="minutes-record__task-title">{task.title}</span>
                <span className="minutes-record__task-status">{task.status ?? 'To Do'}</span>
              </li>
            ))}
          </ul>
        </section>
      ) : null}

      {canEdit && posted && (
        <section className="minutes-modal__follow-up">
          <p className="field-hint">Next steps from this meeting:</p>
          <div className="minutes-modal__follow-up-btns">
            <button type="button" className="btn-secondary btn-sm" onClick={onAddProject}>
              + New project
            </button>
            <button type="button" className="btn-secondary btn-sm" onClick={onAddTask}>
              + New task
            </button>
          </div>
        </section>
      )}
    </article>
  );
}

export function AccountMeetingMinutesPanel({
  client,
  canEdit,
  selection,
  onSelectionChange,
  onAddProject,
  onAddTask,
}: AccountMeetingMinutesPanelProps) {
  const { kind: portal } = usePortal();
  const { data, refresh, username, ownerIdKind } = useCrmData();
  const { profile } = useAuth();

  const minutesForClient = useMemo(
    () =>
      data.meetingMinutes
        .filter((m) => idsMatch(m.client_id, client.id))
        .sort((a, b) => {
          const da = a.meeting_date ?? a.created_at ?? '';
          const db = b.meeting_date ?? b.created_at ?? '';
          return db.localeCompare(da);
        }),
    [data.meetingMinutes, client.id],
  );

  const [draft, setDraft] = useState(emptyDraft);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState(true);

  const selectedId = selection.id;
  const isNew = selection.isNew;

  const selected = useMemo(
    () => minutesForClient.find((m) => m.id === selectedId) ?? null,
    [minutesForClient, selectedId],
  );

  const isPosted = Boolean(selected && isMeetingMinutePosted(selected) && !isNew);
  const viewMode = isPosted && !editing;

  useEffect(() => {
    if (isNew) {
      setDraft(emptyDraft());
      setEditing(true);
      setError(null);
      return;
    }
    if (selected) {
      setDraft(draftFromMinute(selected));
      setEditing(!isMeetingMinutePosted(selected));
      setError(null);
      return;
    }
    if (minutesForClient.length > 0) {
      const first = minutesForClient[0];
      onSelectionChange({ id: first.id, isNew: false });
    } else {
      onSelectionChange({ id: null, isNew: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- sync when list/selection empty
  }, [client.id, isNew, selected?.id, minutesForClient.length]);

  function startNew() {
    onSelectionChange({ id: null, isNew: true });
    setDraft(emptyDraft());
    setEditing(true);
    setError(null);
  }

  function selectMinute(m: MeetingMinute) {
    onSelectionChange({ id: m.id, isNew: false });
    setDraft(draftFromMinute(m));
    setEditing(!isMeetingMinutePosted(m));
    setError(null);
  }

  /** Meeting minutes stay person-owned; clients.owner_id is a salesperson code. */
  function resolveOwnerId(): string {
    const me = data.users.find((u) => u.username === username);
    if (me) return ownerIdForUser(me, ownerIdKind);
    return username;
  }

  function validateDraft(forPost: boolean): string | null {
    if (!draft.title.trim()) return 'Meeting title is required.';
    if (!datetimeLocalSingaporeToIso(draft.meetingLocal)) return 'Meeting date and time are required.';
    if (forPost) {
      if (!draft.attendees.trim()) return 'Attendees are required before posting.';
      if (!draft.discussion.trim()) return 'Discussion is required before posting.';
    }
    return null;
  }

  function buildPayload(forPost: boolean) {
    const now = new Date().toISOString();
    const alreadyPosted = Boolean(selected && isMeetingMinutePosted(selected));
    return {
      title: draft.title.trim(),
      meeting_date: datetimeLocalSingaporeToIso(draft.meetingLocal),
      attendees: draft.attendees.trim(),
      discussion: draft.discussion.trim(),
      status: forPost ? 'posted' : 'draft',
      posted_at: forPost
        ? alreadyPosted && selected?.posted_at
          ? selected.posted_at
          : now
        : null,
      updated_at: now,
    };
  }

  async function persist(forPost: boolean) {
    if (!canEdit) return;

    const validationError = validateDraft(forPost);
    if (validationError) {
      setError(validationError);
      return;
    }

    const author_name = profile?.display_name?.trim() || profile?.username || username;
    setSaving(true);
    setError(null);

    try {
      const payload = buildPayload(forPost);

      if (isNew) {
        const { data: inserted, error: insertError } = await getDbSupabase()
          .from('account_meeting_minutes')
          .insert({
            client_id: client.id,
            ...payload,
            author_name,
            owner_id: resolveOwnerId(),
          })
          .select('id')
          .single();
        if (insertError) throw insertError;
        await refresh();
        const newId = inserted?.id ? String(inserted.id) : null;
        logPortalActivityQuiet({
          portal,
          action: 'create',
          entityType: 'meeting_minutes',
          entityId: newId ?? undefined,
          clientId: client.id,
          title: forPost
            ? `Meeting minutes posted: ${payload.title}`
            : `Meeting minutes draft created: ${payload.title}`,
          detail: `Account: ${client.company_name} (${client.code}).`,
        });
        onSelectionChange({ id: newId, isNew: false });
        setEditing(!forPost);
      } else if (selected) {
        const { error: updateError } = await getDbSupabase()
          .from('account_meeting_minutes')
          .update(payload)
          .eq('id', selected.id);
        if (updateError) throw updateError;
        await refresh();
        logPortalActivityQuiet({
          portal,
          action: forPost ? 'update' : 'update',
          entityType: 'meeting_minutes',
          entityId: selected.id,
          clientId: client.id,
          title: forPost
            ? `Meeting minutes posted: ${payload.title}`
            : `Meeting minutes updated: ${payload.title}`,
          detail: `Account: ${client.company_name} (${client.code}).`,
        });
        setEditing(!forPost);
      }
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!canEdit || !selected || isNew) return;
    if (!window.confirm('Delete this meeting minutes entry?')) return;

    setDeleting(true);
    setError(null);
    try {
      const { error: deleteError } = await getDbSupabase()
        .from('account_meeting_minutes')
        .delete()
        .eq('id', selected.id);
      if (deleteError) throw deleteError;
      logPortalActivityQuiet({
        portal,
        action: 'delete',
        entityType: 'meeting_minutes',
        entityId: selected.id,
        clientId: client.id,
        title: `Meeting minutes deleted: ${selected.title}`,
        detail: `Account: ${client.company_name} (${client.code}).`,
      });
      await refresh();
      startNew();
    } catch (err) {
      setError(formatSupabaseError(err));
    } finally {
      setDeleting(false);
    }
  }

  function minuteForFollowUp(): MeetingMinute | null {
    if (selected && isMeetingMinutePosted(selected)) return selected;
    return null;
  }

  function handleAddProjectClick() {
    const m = minuteForFollowUp();
    if (!m) return;
    onAddProject({ title: projectTitleFromMinute(m) });
  }

  const linkedTasksForSelected = useMemo(
    () =>
      selected
        ? data.tasks.filter((t) => t.meeting_minute_id != null && t.meeting_minute_id === selected.id)
        : [],
    [data.tasks, selected?.id],
  );

  function handleAddTaskClick() {
    const m = minuteForFollowUp();
    if (!m) return;
    onAddTask({
      title: taskTitleFromMinute(m),
      description: taskDescriptionFromMinute(m),
      meetingMinuteId: m.id,
    });
  }

  return (
    <div className="minutes-panel">
      <aside className="minutes-modal__list">
        <div className="minutes-modal__list-head">
          <span className="minutes-modal__list-label">Meetings</span>
          {canEdit && (
            <button type="button" className="account-side-btn" onClick={startNew}>
              New
            </button>
          )}
        </div>
        {minutesForClient.length === 0 ? (
          <p className="account-side-muted">No minutes yet.</p>
        ) : (
          <ul className="minutes-modal__entries">
            {minutesForClient.map((m) => (
              <li key={m.id}>
                <button
                  type="button"
                  className={`minutes-modal__entry${m.id === selectedId && !isNew ? ' minutes-modal__entry--active' : ''}`}
                  onClick={() => selectMinute(m)}
                >
                  <span
                    className={`minutes-modal__entry-badge${isMeetingMinutePosted(m) ? ' minutes-modal__entry-badge--posted' : ''}`}
                  >
                    {minuteStatusLabel(m)}
                  </span>
                  <strong>{m.title}</strong>
                  <span>{formatSingaporeDateTime(m.meeting_date ?? m.created_at)}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </aside>

      <div className="minutes-modal__editor">
        {error && <div className="alert alert-error">{error}</div>}

        {viewMode && selected ? (
          <MeetingMinutesReadOnlyView
            minute={selected}
            linkedTasks={linkedTasksForSelected}
            canEdit={canEdit}
            onEdit={() => setEditing(true)}
            onAddProject={handleAddProjectClick}
            onAddTask={handleAddTaskClick}
          />
        ) : canEdit ? (
          <form
            className="crm-form"
            onSubmit={(e) => {
              e.preventDefault();
              void persist(isPosted);
            }}
          >
            <div className="minutes-record__head minutes-record__head--edit">
              <span
                className={`minutes-record__status-badge${isPosted ? ' minutes-record__status-badge--posted' : ' minutes-record__status-badge--draft'}`}
              >
                {isPosted ? 'Posted · editing' : 'Draft'}
              </span>
            </div>

            <label className="field">
              <span className="field-label">Meeting title *</span>
              <input
                className="field-input"
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
                placeholder="e.g. Site visit, Q2 review"
              />
            </label>

            <label className="field">
              <span className="field-label">Meeting date &amp; time *</span>
              <input
                className="field-input"
                type="datetime-local"
                value={draft.meetingLocal}
                onChange={(e) => setDraft((d) => ({ ...d, meetingLocal: e.target.value }))}
              />
            </label>

            <label className="field">
              <span className="field-label">Attendees {isPosted ? '' : '(required to post)'}</span>
              <textarea
                className="field-input"
                rows={3}
                value={draft.attendees}
                onChange={(e) => setDraft((d) => ({ ...d, attendees: e.target.value }))}
                placeholder="Enter names, roles and companies"
              />
            </label>

            <label className="field">
              <span className="field-label">Discussion {isPosted ? '' : '(required to post)'}</span>
              <textarea
                className="field-input"
                rows={10}
                value={draft.discussion}
                onChange={(e) => setDraft((d) => ({ ...d, discussion: e.target.value }))}
                placeholder="Enter agenda, key points, decisions and action items"
              />
            </label>

            <div className="crm-form__actions">
              {!isNew && selected && (
                <button
                  type="button"
                  className="btn-ghost"
                  disabled={deleting || saving}
                  onClick={() => void handleDelete()}
                >
                  {deleting ? 'Deleting…' : 'Delete'}
                </button>
              )}
              <span className="crm-form__actions-spacer" />
              {isPosted && (
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={saving}
                  onClick={() => {
                    setEditing(false);
                    if (selected) setDraft(draftFromMinute(selected));
                  }}
                >
                  Cancel edit
                </button>
              )}
              {!isPosted && (
                <button
                  type="button"
                  className="btn-secondary"
                  disabled={saving}
                  onClick={() => void persist(false)}
                >
                  {saving ? 'Saving…' : 'Save draft'}
                </button>
              )}
              <button
                type="button"
                className="btn-primary"
                disabled={saving}
                onClick={() => void persist(true)}
              >
                {saving ? 'Saving…' : isPosted ? 'Save & keep posted' : 'Post'}
              </button>
            </div>
          </form>
        ) : selected ? (
          <MeetingMinutesReadOnlyView
            minute={selected}
            linkedTasks={linkedTasksForSelected}
            canEdit={false}
            onEdit={() => {}}
            onAddProject={handleAddProjectClick}
            onAddTask={handleAddTaskClick}
          />
        ) : (
          <p className="account-side-muted">Select a meeting or create a new one.</p>
        )}
      </div>
    </div>
  );
}
